/**
 * Alerts Management Route (Story 27.2).
 *
 * A pixel-faithful clone of the Clients page (routes/_authenticated/clients/
 * index.tsx) — same page shell, table chrome, card grid, search input, view
 * switcher, toolbar. Lists every metric alert across the tenant's clients with
 * create / edit / delete / pause-resume.
 *
 * State ownership (held in the PAGE, identical to clients/index.tsx):
 *  - Search: URL is source of truth (Route.validateSearch); searchInput +
 *    useDebounce(…, 300); re-sync on back/forward.
 *  - View mode: localStorage `alertList_viewMode` (default "grid").
 *  - Sort: derived `effectiveSort` (NOT useState), persisted to
 *    `alertList_sortBy`.
 *  - Body switch: AnimatePresence mode="wait", grid/table motion branches.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { BellRinging, MagnifyingGlass } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { PageSpinner } from "../../../components/PageSpinner";
import { apiGet, apiPatch } from "../../../lib/apiClient";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { usePermission, Actions } from "../../../hooks/usePermission";
import { useUpgradeGuard } from "../../../hooks/useUpgradeGuard";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { useDebounce } from "../../../hooks/useDebounce";
import { countAlertsForGate } from "../../../lib/alertPlanGate";
import { AlertFilterToolbar } from "../../../components/alerts/AlertFilterToolbar";
import type { AlertViewMode } from "../../../components/alerts/ViewSwitcher";
import { AlertCardGrid } from "../../../components/alerts/AlertCardGrid";
import { AlertTableView } from "../../../components/alerts/AlertTableView";
import { AlertFormDialog } from "../../../components/alerts/AlertFormDialog";
import { DeleteAlertDialog } from "../../../components/alerts/DeleteAlertDialog";
import type { Alert, SortOption } from "../../../components/alerts/types";
import { platformDisplayName } from "../../../components/alerts/alertDisplay";
import { ALERT_HIDEABLE_COLUMNS } from "../../../components/alerts/alertColumns";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";

export interface AlertsSearch {
  search?: string;
  sort?: SortOption;
  /** Comma-joined filter params (URL-synced — shareable, survive refresh). */
  status?: string;
  platform?: string;
  client?: string;
}

function optionalStr(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

const VALID_SORTS: readonly SortOption[] = [
  "name_asc",
  "name_desc",
  "created_asc",
  "created_desc",
  "updated_asc",
  "updated_desc",
  "client_asc",
  "client_desc",
  "status_asc",
  "status_desc",
  "platform_asc",
  "platform_desc",
  "interval_asc",
  "interval_desc",
  "last_triggered_asc",
  "last_triggered_desc",
];

/** Cadence order for sorting the Interval column (alphabetical would be wrong). */
const INTERVAL_RANK: Record<string, number> = { day: 0, week: 1, month: 2 };

export const Route = createFileRoute("/_authenticated/alerts/")({
  validateSearch: (search: Record<string, unknown>): AlertsSearch => ({
    search: typeof search.search === "string" ? search.search : undefined,
    sort: VALID_SORTS.includes(search.sort as SortOption)
      ? (search.sort as SortOption)
      : undefined,
    status: optionalStr(search.status),
    platform: optionalStr(search.platform),
    client: optionalStr(search.client),
  }),
  component: AlertsPage,
});

interface ClientOption {
  id: string;
  name: string;
  demoMode: boolean;
}

function AlertsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {
    search: urlSearch,
    sort: urlSort,
    status: urlStatus,
    platform: urlPlatform,
    client: urlClient,
  } = Route.useSearch();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<Alert | null>(null);
  const [lastEditingAlert, setLastEditingAlert] = useState<Alert | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Alert | null>(null);
  const [viewMode, setViewMode] = useState<AlertViewMode>(
    () =>
      (localStorage.getItem("alertList_viewMode") as AlertViewMode) ?? "grid",
  );
  // Hidden table columns — per-device, persisted to localStorage. Shared by the
  // table (hide buttons) and the toolbar's Columns menu (restore).
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "alertTable_hiddenColumns",
    ALERT_HIDEABLE_COLUMNS,
  );
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const { hasPermission } = usePermission();
  const canManage = hasPermission(Actions.MANAGE_CLIENTS);
  const { plan } = usePlanLimits();

  // ── Filters (URL-synced — shareable, survive refresh + back/forward) ──
  const statusFilter = useMemo(
    () => (urlStatus ? urlStatus.split(",").filter(Boolean) : []),
    [urlStatus],
  );
  const platformFilter = useMemo(
    () => (urlPlatform ? urlPlatform.split(",").filter(Boolean) : []),
    [urlPlatform],
  );
  const clientFilter = useMemo(
    () => (urlClient ? urlClient.split(",").filter(Boolean) : []),
    [urlClient],
  );
  const setFilterParam = useCallback(
    (key: "status" | "platform" | "client", values: string[]) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({
          ...prev,
          [key]: values.length > 0 ? values.join(",") : undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );
  const setStatusFilter = useCallback(
    (s: string[]) => setFilterParam("status", s),
    [setFilterParam],
  );
  const setPlatformFilter = useCallback(
    (p: string[]) => setFilterParam("platform", p),
    [setFilterParam],
  );
  const setClientFilter = useCallback(
    (c: string[]) => setFilterParam("client", c),
    [setFilterParam],
  );

  // Toast state
  const [toast, setToast] = useState<{
    type: "success" | "error" | "info";
    message: string;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  function showToast(type: "success" | "error" | "info", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }

  // ── Search state ──────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState(urlSearch ?? "");
  const debouncedSearch = useDebounce(searchInput, 300);

  useEffect(() => {
    setSearchInput(urlSearch ?? "");
  }, [urlSearch]);

  // ── Sort — derived state, NOT useState ────────────────────────────
  const effectiveSort: SortOption =
    urlSort ??
    (localStorage.getItem("alertList_sortBy") as SortOption | null) ??
    "name_asc";

  useEffect(() => {
    localStorage.setItem("alertList_sortBy", effectiveSort);
  }, [effectiveSort]);

  useEffect(() => {
    localStorage.setItem("alertList_viewMode", viewMode);
  }, [viewMode]);

  // ── Linger the editing alert so the dialog can play its exit animation ──
  useEffect(() => {
    if (editingAlert) {
      setLastEditingAlert(editingAlert);
      return;
    }
    if (!lastEditingAlert) return;
    const t = setTimeout(() => setLastEditingAlert(null), 300);
    return () => clearTimeout(t);
  }, [editingAlert, lastEditingAlert]);

  // ── Alerts query ──────────────────────────────────────────────────
  const {
    data: alerts,
    isLoading,
    error,
  } = useQuery<Alert[]>({
    queryKey: ["alerts"],
    queryFn: async () => {
      const res = await apiGet("/api/alerts");
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail ?? "Failed to load alerts");
      }
      const json = await res.json();
      return json.data.alerts as Alert[];
    },
  });

  // ── Clients query (for the create dialog's required Client selector AND the
  //    plan-gate's demo-exclusion input — the clients list carries demoMode,
  //    the alerts list alone does not). Reuses the clients page's ["clients"]
  //    query (cache-shared). ──
  const { data: clientsData } = useQuery<ClientOption[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) return [];
      const json = await res.json();
      // Return the FULL client rows unchanged — this shares the ["clients"]
      // cache with the Clients page; stripping fields here would overwrite that
      // cache with a reduced shape and break the Clients page until a refetch.
      return json.data as ClientOption[];
    },
  });
  const clientOptions = useMemo<ClientOption[]>(
    () =>
      (clientsData ?? []).map((c) => ({
        id: c.id,
        name: c.name,
        demoMode: !!c.demoMode,
      })),
    [clientsData],
  );

  // ── Filtering + sort pipeline ─────────────────────────────────────
  const filteredAlerts = useMemo(() => {
    if (!alerts) return [];
    let result = [...alerts];

    if (statusFilter.length > 0) {
      result = result.filter((a) =>
        statusFilter.includes(a.isActive ? "active" : "paused"),
      );
    }
    if (platformFilter.length > 0) {
      result = result.filter((a) => platformFilter.includes(a.platform));
    }
    if (clientFilter.length > 0) {
      result = result.filter((a) => clientFilter.includes(a.clientId));
    }

    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter((a) => a.name.toLowerCase().includes(q));
    }

    switch (effectiveSort) {
      case "name_asc":
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name_desc":
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case "created_asc":
        result.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case "created_desc":
        result.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "updated_asc":
        result.sort((a, b) => a.updatedAt - b.updatedAt);
        break;
      case "updated_desc":
        result.sort((a, b) => b.updatedAt - a.updatedAt);
        break;
      case "client_asc":
        result.sort((a, b) => a.clientName.localeCompare(b.clientName));
        break;
      case "client_desc":
        result.sort((a, b) => b.clientName.localeCompare(a.clientName));
        break;
      // Status — active first (asc); tie-break by name for a stable order.
      case "status_asc":
        result.sort(
          (a, b) =>
            (a.isActive ? 0 : 1) - (b.isActive ? 0 : 1) ||
            a.name.localeCompare(b.name),
        );
        break;
      case "status_desc":
        result.sort(
          (a, b) =>
            (b.isActive ? 0 : 1) - (a.isActive ? 0 : 1) ||
            a.name.localeCompare(b.name),
        );
        break;
      // Platform — by the human label shown, not the raw slug.
      case "platform_asc":
        result.sort((a, b) =>
          platformDisplayName(a.platform).localeCompare(
            platformDisplayName(b.platform),
          ),
        );
        break;
      case "platform_desc":
        result.sort((a, b) =>
          platformDisplayName(b.platform).localeCompare(
            platformDisplayName(a.platform),
          ),
        );
        break;
      // Interval — cadence order (day < week < month), not alphabetical.
      case "interval_asc":
        result.sort(
          (a, b) =>
            (INTERVAL_RANK[a.interval] ?? 99) -
            (INTERVAL_RANK[b.interval] ?? 99),
        );
        break;
      case "interval_desc":
        result.sort(
          (a, b) =>
            (INTERVAL_RANK[b.interval] ?? 99) -
            (INTERVAL_RANK[a.interval] ?? 99),
        );
        break;
      // Last triggered — by timestamp; never-triggered (null) always sorts last.
      case "last_triggered_asc":
        result.sort((a, b) => {
          if (a.lastTriggeredAt == null && b.lastTriggeredAt == null) return 0;
          if (a.lastTriggeredAt == null) return 1;
          if (b.lastTriggeredAt == null) return -1;
          return a.lastTriggeredAt - b.lastTriggeredAt;
        });
        break;
      case "last_triggered_desc":
        result.sort((a, b) => {
          if (a.lastTriggeredAt == null && b.lastTriggeredAt == null) return 0;
          if (a.lastTriggeredAt == null) return 1;
          if (b.lastTriggeredAt == null) return -1;
          return b.lastTriggeredAt - a.lastTriggeredAt;
        });
        break;
    }

    return result;
  }, [
    alerts,
    statusFilter,
    platformFilter,
    clientFilter,
    debouncedSearch,
    effectiveSort,
  ]);

  // Filter option lists derived from loaded alerts.
  const platformFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: { value: string; label: string }[] = [];
    for (const a of alerts ?? []) {
      if (!seen.has(a.platform)) {
        seen.add(a.platform);
        out.push({ value: a.platform, label: platformDisplayName(a.platform) });
      }
    }
    return out;
  }, [alerts]);
  const clientFilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of alerts ?? []) map.set(a.clientId, a.clientName);
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [alerts]);

  // Page-level plan count: free = tenant-total non-demo alerts; paid = 0 here
  // (the real per-client check happens at dialog submit). `undefined` while the
  // alerts/clients queries load → the guard refuses + the button disables.
  // Mirrors the server getAlertCount scope (27.4) via the shared algebra.
  const pageAlertCount = countAlertsForGate(alerts, clientOptions, plan);

  // ── Handlers ──────────────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, search: value || undefined }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSortChange = useCallback(
    (value: SortOption) => {
      navigate({
        from: Route.fullPath,
        search: (prev) => ({ ...prev, sort: value }),
      });
    },
    [navigate],
  );

  const clearAllFilters = useCallback(() => {
    setSearchInput("");
    navigate({
      from: Route.fullPath,
      search: (prev) => ({
        ...prev,
        search: undefined,
        status: undefined,
        platform: undefined,
        client: undefined,
      }),
    });
  }, [navigate]);

  // Upgrade guard for the Create button. Free at the tenant-wide limit → modal;
  // paid users (count 0 here) open the dialog where the per-client check fires.
  const { guardAction, UpgradeModal } = useUpgradeGuard({
    feature: "alertLimit",
    currentCount: pageAlertCount,
  });

  function handleCreateClick() {
    guardAction(() => {
      setCreateOpen(true);
    });
  }

  // ── Active/paused toggle mutation ─────────────────────────────────
  const toggleActive = useMutation({
    mutationFn: async (alert: Alert) => {
      const res = await apiPatch(`/api/alerts/${alert.id}`, {
        isActive: !alert.isActive,
      });
      const json = await res
        .json()
        .catch(() => ({ detail: "Server error. Please try again." }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onMutate: async (alert) => {
      await queryClient.cancelQueries({ queryKey: ["alerts"] });
      const previous = queryClient.getQueryData<Alert[]>(["alerts"]);
      queryClient.setQueryData<Alert[]>(["alerts"], (old) =>
        old?.map((a) =>
          a.id === alert.id ? { ...a, isActive: !a.isActive } : a,
        ) ?? [],
      );
      return { previous };
    },
    onError: (err: any, _alert, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["alerts"], context.previous);
      }
      showToast("error", err?.title ?? err?.detail ?? "Failed to update alert");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  function handleEditClick(alert: Alert) {
    setEditingAlert(alert);
  }

  const hasAlerts = !!alerts && alerts.length > 0;

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Alerts" }]} />
      <div className="mb-6" />

      {/* Filter toolbar — shown when alerts exist */}
      {!isLoading && !error && hasAlerts && (
        <AlertFilterToolbar
          search={searchInput}
          onSearchChange={handleSearchChange}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          platformFilter={platformFilter}
          onPlatformFilterChange={setPlatformFilter}
          clientFilter={clientFilter}
          onClientFilterChange={setClientFilter}
          platformOptions={platformFilterOptions}
          clientOptions={clientFilterOptions}
          canManage={canManage}
          onCreate={handleCreateClick}
          hiddenColumns={hiddenColumns}
          onToggleColumn={toggleColumn}
          showColumnsControl={viewMode === "table"}
        />
      )}

      {isLoading && <PageSpinner />}

      {error && (
        <div
          className="rounded-md border border-destructive bg-destructive/5 p-4 text-sm text-destructive"
          role="alert"
        >
          {error.message}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && alerts && alerts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="rounded-full bg-primary/10 p-4 mb-4">
            <BellRinging size={32} className="text-primary" />
          </div>
          <h2 className="text-lg font-semibold mb-2">No alerts yet</h2>
          <p className="text-muted-foreground mb-6 max-w-sm">
            Create an alert to get notified when a client metric crosses a
            threshold.
          </p>
          {canManage && (
            <Button
              variant="brand"
              className="rounded-2xl text-[13px] cursor-pointer"
              onClick={handleCreateClick}
            >
              <BellRinging size={16} className="mr-2" />
              Create your first alert
            </Button>
          )}
        </div>
      )}

      {/* No-results state (alerts exist but search matches none) */}
      {!isLoading && !error && hasAlerts && filteredAlerts.length === 0 && (
        <div className="text-center py-12">
          <MagnifyingGlass
            size={48}
            className="mx-auto text-muted-foreground mb-4"
          />
          <h3 className="text-lg font-medium">No alerts match your search</h3>
          <p className="mt-1" style={{ color: "#5A7396" }}>
            Try adjusting your search.
          </p>
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="mt-4 rounded-2xl border-[0.5px] border-input text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
          >
            Clear all filters
          </Button>
        </div>
      )}

      {/* Body */}
      {!isLoading && !error && hasAlerts && filteredAlerts.length > 0 && (
        <AnimatePresence mode="wait">
          {viewMode === "grid" ? (
            <motion.div
              key="grid"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <AlertCardGrid
                alerts={filteredAlerts}
                canManage={canManage}
                onAlertClick={canManage ? handleEditClick : undefined}
                onEditClick={handleEditClick}
                onDeleteClick={(alert) => setDeleteTarget(alert)}
                onToggleActive={(alert) => toggleActive.mutate(alert)}
                triggerRefs={triggerRefs}
              />
            </motion.div>
          ) : (
            <motion.div
              key="table"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <AlertTableView
                alerts={filteredAlerts}
                canManage={canManage}
                onAlertClick={canManage ? handleEditClick : undefined}
                onEditClick={handleEditClick}
                onDeleteClick={(alert) => setDeleteTarget(alert)}
                onToggleActive={(alert) => toggleActive.mutate(alert)}
                triggerRefs={triggerRefs}
                sortBy={effectiveSort}
                onSortChange={handleSortChange}
                hiddenColumns={hiddenColumns}
                onHideColumn={toggleColumn}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* Create dialog */}
      {canManage && (
        <AlertFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          clients={clientOptions ?? []}
          existingAlerts={alerts ?? []}
          onSaved={() => showToast("success", "Alert created")}
        />
      )}

      {/* Edit dialog — lingering lastValue so AnimatePresence can exit */}
      {canManage && lastEditingAlert && (
        <AlertFormDialog
          open={editingAlert !== null}
          onOpenChange={(o) => !o && setEditingAlert(null)}
          clients={clientOptions ?? []}
          alert={lastEditingAlert}
          existingAlerts={alerts ?? []}
          onSaved={() => showToast("success", "Alert updated")}
        />
      )}

      {/* Delete confirmation dialog */}
      <DeleteAlertDialog
        alert={deleteTarget}
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null);
          showToast("success", "Alert deleted");
        }}
        showToast={showToast}
      />

      {/* Toast notification */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-50 rounded-md px-4 py-3 text-sm shadow-lg ${
            toast.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : toast.type === "info"
                ? "bg-blue-50 border border-blue-200 text-blue-800"
                : "bg-destructive/10 border border-destructive/20 text-destructive"
          }`}
          role={toast.type === "error" ? "alert" : "status"}
        >
          {toast.message}
        </div>
      )}

      {UpgradeModal}
    </div>
  );
}
